import { articlesByCategory } from '@/lib/articles';
import { HELP_CENTER_HOME_CATEGORY_IDS } from '@/lib/help-center-home-categories';

/** Segmentos já usados por outras rotas da app — não são categorias de guias. */
export const APP_ROUTE_RESERVED_SEGMENTS = new Set([
  'powers',
  'aces',
  'painel',
  'admin',
  'workspace',
  'changelog',
  'equipe',
  'conduta',
  'regras',
  'termos',
  'privacidade',
  'smileytest',
  'api',
  '_next',
]);

/**
 * A categoria de guias «powers» colide com /powers (catálogo).
 * Na URL pública usa-se `powers-ajuda`.
 */
const GUIDE_CATEGORY_PATH_ALIASES: Record<string, string> = {
  powers: 'powers-ajuda',
};

const GUIDE_CATEGORY_ID_FROM_PATH: Record<string, string> = Object.fromEntries(
  Object.entries(GUIDE_CATEGORY_PATH_ALIASES).map(([id, path]) => [path, id])
);

export function slugifyGuideTitle(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getGuideCategoryPath(categoryId: string): string {
  return GUIDE_CATEGORY_PATH_ALIASES[categoryId] ?? categoryId;
}

export function resolveGuideCategoryId(pathSegment: string): string | null {
  const key = pathSegment.toLowerCase();
  if (GUIDE_CATEGORY_ID_FROM_PATH[key]) return GUIDE_CATEGORY_ID_FROM_PATH[key];
  if (APP_ROUTE_RESERVED_SEGMENTS.has(key)) return null;
  if (key in articlesByCategory) return key;
  if (HELP_CENTER_HOME_CATEGORY_IDS.has(key)) return key;
  return null;
}

export function isGuideCategoryPath(pathSegment: string): boolean {
  return resolveGuideCategoryId(pathSegment) !== null;
}

export function getGuideCategoryHref(categoryId: string): string {
  return `/${getGuideCategoryPath(categoryId)}`;
}

export function getGuideHref(categoryId: string, articleSlug: string): string {
  return `/${getGuideCategoryPath(categoryId)}/${articleSlug}`;
}

export type StaticGuideArticle = {
  id: number;
  slug: string;
  title: string;
  updatedAt: string;
  popular: boolean;
};

export function getStaticArticlesForCategory(categoryId: string): StaticGuideArticle[] {
  return articlesByCategory[categoryId as keyof typeof articlesByCategory] ?? [];
}

export function findStaticGuideArticle(
  categoryId: string,
  slugOrId: string
): StaticGuideArticle | null {
  const list = getStaticArticlesForCategory(categoryId);
  if (list.length === 0) return null;

  const key = slugOrId.trim().toLowerCase();
  const bySlug = list.find((a) => a.slug === key);
  if (bySlug) return bySlug;

  const asNum = Number.parseInt(key, 10);
  if (!Number.isNaN(asNum)) {
    return list.find((a) => a.id === asNum) ?? null;
  }

  return null;
}
